/**
 * FIX 4 / G9+G10 — pure header cart navigate args.
 * Frozen location.state intent only; never spread prior state; never hub+openCart.
 */

import {
  HEADER_HUB_EMPTY_CART_STATE,
  type HubEmptyCartLocationState,
} from './hubEmptyCartIntent.js';

export interface OpenCartLocationState {
  readonly openCart: true;
  readonly openCartSource: 'header';
}

/** Frozen openCart intent — ONLY these keys (G9). */
export const HEADER_OPEN_CART_STATE: OpenCartLocationState = {
  openCart: true,
  openCartSource: 'header',
};

export interface HeaderOpenCartNavigateArgs {
  readonly to: string;
  /** Same-route reopen uses replace so intent is applied without stacking history. */
  readonly replace: boolean;
  /**
   * Frozen `{ openCart, openCartSource }` when destination is a valid SP shop route.
   * Frozen `{ openHubEmptyCart }` when destination is Wave D hub (G10 — never shop openCart).
   */
  readonly state: OpenCartLocationState | HubEmptyCartLocationState;
}

export interface BuildHeaderOpenCartNavigateArgsInput {
  readonly currentPathname: string;
  readonly currentSearch: string;
  /**
   * From `resolveListableLastKnownSalesPointHref` — null when missing or
   * non-listable (Wave D hub; no shop mount / openCart).
   */
  readonly lastKnownSalesPointHref: string | null;
  /** Hub fallback when lastKnown is null (`resolvePurchasesNavHref` / Wave D). */
  readonly hubHref: string;
}

function isSameRoute(
  currentPathname: string,
  currentSearch: string,
  href: string,
): boolean {
  return currentPathname + currentSearch === href || currentPathname === href;
}

/**
 * Build navigate `to` / `replace` / frozen `state` for header cart open.
 * G9: never merge prior location.state.
 * G10: hub destination → no shop openCart intent (shell HubEmptyCartSheet instead).
 */
export function buildHeaderOpenCartNavigateArgs(
  input: BuildHeaderOpenCartNavigateArgsInput,
): HeaderOpenCartNavigateArgs {
  const { currentPathname, currentSearch, lastKnownSalesPointHref, hubHref } =
    input;

  if (lastKnownSalesPointHref === null) {
    const to = hubHref;
    return {
      to,
      replace: isSameRoute(currentPathname, currentSearch, to),
      state: HEADER_HUB_EMPTY_CART_STATE,
    };
  }

  const to = lastKnownSalesPointHref;
  return {
    to,
    replace: isSameRoute(currentPathname, currentSearch, to),
    state: HEADER_OPEN_CART_STATE,
  };
}
